// externals

// libraries
import type { Cludes, interfaces, IShortDisclaimerData } from '@makemydeal/dr-common-utils';
import type { IPencilOfferInputs, TermsById } from '@makemydeal/dr-platform-types';
import { FINANCE, OFFER_TYPE } from '@makemydeal/dr-platform-types';
import type { OfferReduxBaseState } from '../types/offerReduxBaseState';

// utils
import utilsHasOfferPriceChanged from '../utils/hasOfferPriceChanged';
import { getAcquisitionFee, getDueAtSigning, hasDmvFees } from './feesSelectors';
import { getCurrentOfferType, getDownPaymentByOfferType, getOfferDetails } from './offerNodeSelectors';
import { getAnnualMiles, getSellRate, isOnePayLease as getOnePayLease } from './selectedTermSelectors';
import { getAllTermsFromOffer } from './slices/offerSliceSelectors';
import { getTotalTax } from './taxSelectors';
import { getDisclosableTotalPayment } from './utils/nonSelectorUtils';
import type { LeaseDueAtSigningLine } from '../utils/leaseDueAtSigningBreakdown';
import { getLeaseDueAtSigningBreakdown } from '../utils/leaseDueAtSigningBreakdown';

export type LeaseDueAtSigningDisclaimerData = {
    paidAtSigning?: number;
    dueAtSigningItems?: LeaseDueAtSigningLine[];
    remainderAddedToLeasePrice?: number;
};

export const getLeaseDueAtSigningDisclaimerData = (state: OfferReduxBaseState): LeaseDueAtSigningDisclaimerData => {
    const breakdown = getLeaseDueAtSigningBreakdown(state, getCurrentOfferType(state));
    if (!breakdown) {
        return {};
    }

    const remainder = breakdown.dueAtSigning.items.find((item) => item.key === 'remainderAddedToLeasePrice');
    return {
        paidAtSigning: breakdown.estTotalDueAtSigning,
        dueAtSigningItems: breakdown.dueAtSigning.items,
        remainderAddedToLeasePrice: remainder ? -remainder.value : 0
    };
};

export const INCLUDES = 'Includes';
export const EXCLUDES = 'Excludes';
export type GetDisclaimerCludesResult = typeof INCLUDES | typeof EXCLUDES;

export function getDisclaimerCludes(
    offerType: string,
    offer: IPencilOfferInputs,
    termsForOffer: TermsById
): GetDisclaimerCludesResult {
    const totalTax: number = offerType === OFFER_TYPE.cash ? offer?.totalTax || 0 : termsForOffer?.[offer.term!]?.totalTax || 0;
    const dmvFees: number = offer?.dmvFees || 0;
    const dealerFees: number = offer?.dealerFeesTotal || 0;
    const totalFees: number = totalTax + dmvFees + dealerFees;
    return totalFees > 0 ? 'Includes' : 'Excludes';
}

export function getShortDisclaimerData(state: OfferReduxBaseState): IShortDisclaimerData {
    const offerType = getCurrentOfferType(state);
    const offer = getOfferDetails(state, offerType);
    const totalTax = getTotalTax(state, offerType);
    const hasOfferPriceChanged = utilsHasOfferPriceChanged(offer);
    const termsForOffer = getAllTermsFromOffer(state.offer);
    const termMonthsVal = termsForOffer?.[offer.term!]?.months;
    const termMonths = `${termMonthsVal}`;
    const offerPrice = offer?.offerPrice;
    const downpaymentVal = getDownPaymentByOfferType(state, offerType);
    const downPayment = `${downpaymentVal}`;
    const deductDueAtSigningDealerPref = state?.dealer?.leaseSettings?.isDueAtSigninAmountDeduct;
    const programEndDateVal = termsForOffer?.[offer.term!]?.lenderProgram?.programEndDate;
    const programEndDate = `${programEndDateVal}`;
    const cludes = getDisclaimerCludes(offerType, offer, termsForOffer);
    const applyTaxesAndFeesNewOptions = state?.dealer?.applyTaxesAndFeesNewOptions;
    const sellRate = getSellRate(state);
    const sellRateAsString = sellRate.toString();

    let apr;
    let annualMiles;
    offerType === FINANCE ? (apr = sellRateAsString) : (annualMiles = getAnnualMiles(state));

    const isOnePayLease = getOnePayLease(state);
    const acqLoanFee = isOnePayLease ? getAcquisitionFee(termsForOffer[offer.term!]) : undefined;
    const capCostTotal = isOnePayLease ? `${termsForOffer?.[offer.term!]?.capCostTotal}` : undefined;

    return {
        offerType,
        hasOfferPriceChanged,
        downPayment,
        termMonths,
        offerPrice,
        apr,
        annualMiles,
        deductDueAtSigningDealerPref,
        programEndDate,
        cludes, // TODO: There's a mismatch in types, Cludes should probably be changed to a string type instead of enum to resolve
        applyTaxesAndFeesNewOptions,
        totalTax,
        isOnePayLease,
        acqLoanFee,
        capCostTotal
    };
}

export function getLongDisclaimerData(
    state: OfferReduxBaseState
): interfaces.ILongDisclaimerData & LeaseDueAtSigningDisclaimerData {
    const offerType = getCurrentOfferType(state);
    const offer = getOfferDetails(state, offerType);
    const hasOfferPriceChanged = utilsHasOfferPriceChanged(offer);
    const totalTax = getTotalTax(state, offerType);
    const termsForOffer = getAllTermsFromOffer(state.offer);
    const termMonths = `${termsForOffer?.[offer.term!]?.months}`;
    const offerPrice = offer.offerPrice;
    const lender = `${termsForOffer?.[offer.term!]?.lenderLegalName}`;
    const cludes = getDisclaimerCludes(offerType, offer, termsForOffer) as Cludes;
    let monthlyPayment = `${termsForOffer?.[offer.term!]?.monthlyPayment}`;
    const downPayment = `${getDownPaymentByOfferType(state, offerType)}`;
    const programEndDate = `${termsForOffer?.[offer.term!]?.lenderProgram?.programEndDate}`;
    const totalPaymentAmount = getDisclosableTotalPayment(
        termsForOffer?.[offer.term!]?.baseMonthlyPaymentsTotal,
        monthlyPayment,
        termMonths
    );
    const deductDueAtSigningDealerPref = state?.dealer?.leaseSettings?.isDueAtSigninAmountDeduct;
    const dealerName = state?.dealer?.name;
    const applyTaxesAndFeesNewOptions = state?.dealer?.applyTaxesAndFeesNewOptions;
    const hasDealerFees = hasDmvFees(state);
    const sellRate = getSellRate(state);
    const sellRateAsString = sellRate.toString();

    let apr;
    let monthlyAprCost;
    let annualMiles;
    let acqLoanFee;
    let capCostTotal;
    let capCostReduction;
    let securityDeposit;
    let excessMileageCharge;
    let isOnePayLease;
    if (offerType === FINANCE) {
        apr = sellRateAsString;
        const amountFinanced = `${termsForOffer?.[offer.term!]?.amountFinanced}`;
        monthlyAprCost = (Number(monthlyPayment) / Number(amountFinanced)) * 1000;
    } else if (offerType === OFFER_TYPE.lease) {
        annualMiles = getAnnualMiles(state);
        acqLoanFee = getAcquisitionFee(termsForOffer[offer.term!]);
        capCostTotal = `${termsForOffer?.[offer.term!]?.capCostTotal}`;
        capCostReduction = `${termsForOffer?.[offer.term!]?.capCostReduction}`;
        securityDeposit = `${termsForOffer?.[offer.term!]?.securityDeposit}`;
        excessMileageCharge = `${termsForOffer?.[offer.term!]?.excessMileageCharge}`;

        if (getOnePayLease(state)) {
            monthlyPayment = `${getDueAtSigning(state, offerType)}`;
            isOnePayLease = true;
        }
    }

    return {
        offerType,
        hasOfferPriceChanged,
        downPayment,
        termMonths,
        offerPrice,
        lender,
        cludes,
        applyTaxesAndFeesNewOptions,
        apr,
        hasDealerFees,
        monthlyAprCost,
        annualMiles,
        monthlyPayment,
        acqLoanFee,
        capCostTotal,
        capCostReduction,
        securityDeposit,
        excessMileageCharge,
        deductDueAtSigningDealerPref,
        programEndDate,
        totalPaymentAmount,
        dealerName,
        totalTax,
        isOnePayLease,
        ...getLeaseDueAtSigningDisclaimerData(state)
    };
}
